/**
 * The tour's spotlight and callout (SPEC §8, reference 07): a dim scrim with a
 * cutout and an accent outline around the step's panel, and a callout with the
 * step count, dots, Back / Next and Skip. Esc ends the tour.
 *
 * Every button dispatches a `tour.*` command; the tour's own state decides
 * what's shown.
 */
import { usePointerKind } from "../hooks/usePointerKind";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useEditor } from "../EditorContext";
import { useTour } from "../hooks/editorHooks";
import { calloutPlacement } from "./calloutPlacement";
import type { Box } from "./calloutPlacement";
import { TOUR_STEPS } from "./steps";
import type { TourStep } from "./steps";
import { usePanelRects } from "./TourTargets";
import "../ui/controls.css";
import "./tour.css";

/** Used before the callout has been measured. */
const ESTIMATED_CALLOUT = { width: 400, height: 220 };

export function TourOverlay() {
  const tour = useTour();
  const step = tour.active ? TOUR_STEPS[tour.step] : undefined;
  if (!step) return null;
  // One Spotlight for the whole tour (no per-step key): it keeps the last
  // target's box until the next is measured, so the cutout slides between panels.
  return <Spotlight step={step} index={tour.step} />;
}

function Spotlight({ step, index }: { step: TourStep; index: number }) {
  const pointer = usePointerKind();
  const { dispatch } = useEditor();
  const target = useTargetRect(step);
  const calloutRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const size = useElementSize(calloutRef) ?? ESTIMATED_CALLOUT;

  const run = (type: string) => {
    void dispatch({ type, payload: {}, origin: "tour" });
  };
  useEscapeToEndTour();

  // Focus the primary action on each step, so the tour is keyboard-drivable.
  useEffect(() => {
    nextRef.current?.focus();
  }, [index]);

  const screen = { width: window.innerWidth, height: window.innerHeight };
  const placement = target
    ? calloutPlacement(target, size, screen)
    : { left: (screen.width - size.width) / 2, top: (screen.height - size.height) / 2 };
  const next = TOUR_STEPS[index + 1];

  return (
    <div className="kn-tour">
      {target ? (
        <div
          className="kn-tour__spotlight"
          style={{ left: target.left, top: target.top, width: target.width, height: target.height }}
        />
      ) : (
        <div className="kn-tour__scrim" />
      )}
      <div
        ref={calloutRef}
        className="kn-tour__callout"
        role="dialog"
        aria-modal="false"
        aria-labelledby="kn-tour-title"
        aria-describedby="kn-tour-body"
        style={{ left: placement.left, top: placement.top }}
      >
        <div className="kn-tour__meta">
          <span className="kn-tour__count">
            Step {index + 1} of {TOUR_STEPS.length}
          </span>
          <span className="kn-tour__dots" aria-hidden="true">
            {TOUR_STEPS.map((each, i) => (
              <span key={each.id} data-current={i === index || undefined} />
            ))}
          </span>
        </div>
        <h2 id="kn-tour-title">{step.title}</h2>
        <p id="kn-tour-body">{pointer === "coarse" ? (step.touchBody ?? step.body) : step.body}</p>
        <div className="kn-tour__actions">
          <button
            type="button"
            className="kn-tour__skip"
            onClick={() => {
              run("tour.end");
            }}
          >
            Skip tour
          </button>
          <span className="kn-tour__spacer" />
          <button
            type="button"
            className="kn-button"
            disabled={index === 0}
            onClick={() => {
              run("tour.prev");
            }}
          >
            Back
          </button>
          <button
            ref={nextRef}
            type="button"
            className="kn-button kn-button--primary"
            onClick={() => {
              run("tour.next");
            }}
          >
            {next ? `Next: ${next.shortName}` : "Finish"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────

/**
 * The step's target rect, followed every frame while the step shows.
 *
 * Its panel moves for many reasons: the window resizes (and the dock lays
 * out in its own resize callback, after a window resize event), a dock
 * divider is dragged, a phone's sheet slides or the phone turns. Events
 * would each miss some of these, so the rect is read once a frame (one
 * layout read, only during the tour) and state changes only when it moves.
 */
function useTargetRect(step: TourStep): Box | null {
  const registry = usePanelRects();
  const [rect, setRect] = useState<Box | null>(null);

  useLayoutEffect(() => {
    if (!registry) return;
    let frame = 0;
    const follow = () => {
      const measured = registry.rectOf(step.target);
      const box = measured && measured.width > 0 && measured.height > 0 ? toBox(measured) : null;
      setRect((current) => (sameBox(current, box) ? current : box));
      frame = requestAnimationFrame(follow);
    };

    // From the next frame: the step may have just brought its panel forward.
    frame = requestAnimationFrame(follow);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [registry, step]);

  return rect;
}

/** Whether two rects are the same to the pixel (sub-pixel jitter isn't a move). */
function sameBox(a: Box | null, b: Box | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height)
  );
}

function useElementSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setSize({ width: element.offsetWidth, height: element.offsetHeight });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return size;
}

/** Esc ends the tour from anywhere (SPEC §8). */
function useEscapeToEndTour(): void {
  const { dispatch } = useEditor();
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") void dispatch({ type: "tour.end", payload: {}, origin: "tour" });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [dispatch]);
}

function toBox(rect: DOMRect): Box {
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}
